import { Controller } from "@hotwired/stimulus";

// Renders a component inside an iframe whose srcdoc clones the parent page's
// stylesheets and scripts. The iframe has its own viewport, so Tailwind media
// queries respond to resize. Drag the right-edge handle to change width.
export default class extends Controller {
  static targets = ["source", "frame", "viewport", "handle", "widthLabel"];
  static values = { minWidth: { type: Number, default: 320 } };

  connect() {
    this.renderIframe();
    this.observeDarkMode();
    this.bindDrag();
  }

  disconnect() {
    if (this.htmlObserver) this.htmlObserver.disconnect();
    if (this.frameObserver) this.frameObserver.disconnect();
    this.unbindDrag();
  }

  renderIframe() {
    const html = this.buildIframeDocument();
    this.frameTarget.srcdoc = html;
    this.frameTarget.addEventListener("load", () => this.onFrameLoad(), {
      once: true,
    });
  }

  buildIframeDocument() {
    const head = this.cloneParentHead();
    const body = this.sourceTarget.innerHTML;
    const htmlClass = document.documentElement.className || "";
    // Mirror the parent body's classes (e.g. `text-mode bg-mode`) so the iframe
    // inherits the same base text/background styling as the outer page. Strip
    // `min-h-screen` so the iframe sizes to its content instead of 100vh.
    const bodyClass = (document.body.className || "")
      .replace(/\bmin-h-screen\b/g, "")
      .trim();
    const baseHref = document.baseURI;

    return `<!DOCTYPE html>
<html class="${this.escapeAttr(htmlClass)}">
  <head>
    <base href="${this.escapeAttr(baseHref)}">
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    ${head}
    <style>
      html, body { margin: 0; }
      html { padding: 0; }
      body { padding: 20px; overflow-x: hidden; }
    </style>
  </head>
  <body class="${this.escapeAttr(bodyClass)}">${body}</body>
</html>`;
  }

  cloneParentHead() {
    // Clone stylesheets and font preloads.
    const styleNodes = document.head.querySelectorAll(
      'link[rel="stylesheet"], style, link[rel="preload"][as="font"], link[rel="preload"][as="style"]',
    );

    // Clone importmap and module scripts so Stimulus boots inside the iframe.
    const scriptNodes = document.head.querySelectorAll(
      'script[type="importmap"], script[type="module"], link[rel="modulepreload"]',
    );

    return [...styleNodes, ...scriptNodes].map((n) => n.outerHTML).join("\n");
  }

  escapeAttr(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;");
  }

  onFrameLoad() {
    this.updateWidthLabel();
    this.observeFrameHeight();
  }

  observeFrameHeight() {
    const doc = this.frameTarget.contentDocument;
    if (!doc || !doc.body) return;

    const update = () => {
      this.frameTarget.style.height = `${doc.body.scrollHeight}px`;
    };

    update();

    if (this.frameObserver) this.frameObserver.disconnect();
    this.frameObserver = new ResizeObserver(update);
    this.frameObserver.observe(doc.body);
  }

  updateWidthLabel() {
    if (!this.hasWidthLabelTarget) return;
    const width = Math.round(this.viewportTarget.getBoundingClientRect().width);
    this.widthLabelTarget.textContent = `${width}px ${this.breakpointName(width)}`;
  }

  breakpointName(width) {
    // Tailwind default breakpoints.
    if (width >= 1536) return "2xl";
    if (width >= 1280) return "xl";
    if (width >= 1024) return "lg";
    if (width >= 768) return "md";
    if (width >= 640) return "sm";
    return "xs";
  }

  observeDarkMode() {
    this.htmlObserver = new MutationObserver(() => this.syncDarkMode());
    this.htmlObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  syncDarkMode() {
    const doc = this.frameTarget.contentDocument;
    if (!doc || !doc.documentElement) return;
    doc.documentElement.className = document.documentElement.className;
  }

  bindDrag() {
    this.boundStart = this.startDrag.bind(this);
    this.boundMove = this.moveDrag.bind(this);
    this.boundEnd = this.endDrag.bind(this);

    this.handleTarget.addEventListener("pointerdown", this.boundStart);
  }

  unbindDrag() {
    this.handleTarget.removeEventListener("pointerdown", this.boundStart);
    this.handleTarget.removeEventListener("pointermove", this.boundMove);
    this.handleTarget.removeEventListener("pointerup", this.boundEnd);
    this.handleTarget.removeEventListener("pointercancel", this.boundEnd);
  }

  startDrag(event) {
    event.preventDefault();
    this.dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth: this.viewportTarget.getBoundingClientRect().width,
      // The stage (viewport's parent) defines the max draggable width.
      maxWidth: this.viewportTarget.parentElement.getBoundingClientRect().width,
    };
    // Capture the pointer so events keep flowing even when the cursor moves
    // over the iframe, which would otherwise swallow them.
    this.handleTarget.setPointerCapture(event.pointerId);
    this.handleTarget.addEventListener("pointermove", this.boundMove);
    this.handleTarget.addEventListener("pointerup", this.boundEnd);
    this.handleTarget.addEventListener("pointercancel", this.boundEnd);
  }

  moveDrag(event) {
    if (!this.dragState) return;
    const delta = event.clientX - this.dragState.startX;
    const proposed = this.dragState.startWidth + delta;
    const clamped = Math.max(
      this.minWidthValue,
      Math.min(proposed, this.dragState.maxWidth),
    );
    this.viewportTarget.style.width = `${clamped}px`;
    this.updateWidthLabel();
  }

  endDrag() {
    if (
      this.dragState &&
      this.handleTarget.hasPointerCapture(this.dragState.pointerId)
    ) {
      this.handleTarget.releasePointerCapture(this.dragState.pointerId);
    }
    this.dragState = null;
    this.handleTarget.removeEventListener("pointermove", this.boundMove);
    this.handleTarget.removeEventListener("pointerup", this.boundEnd);
    this.handleTarget.removeEventListener("pointercancel", this.boundEnd);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }
}
